/**
 * Single source of routing truth for the Create Diligent Search Form workflow.
 * forms[].sourceUrl is the ORIGINAL source; ingest/convert into MISC storage and serve the hosted copy.
 * forms[].todo blocks availability until resolved. forms[].confirmNote is a soft client-facing note.
 */

export type FormCategory = 'state-specific' | 'generic' | 'optional' | 'none';

export interface StateForm {
  label: string;
  /** Original source (ingest, do not hotlink at runtime). */
  sourceUrl?: string;
  /** Runtime path under public/ once the file is hosted. */
  hostedPath?: string;
  /** External URL when the form is not a hostable PDF. */
  externalUrl?: string;
  /** true = render the shared generic Diligent Effort Form */
  useGenericTemplate?: boolean;
  /** Hard blocker — form is unavailable until resolved */
  todo?: string;
  /** Soft product confirmation note — does not block availability */
  confirmNote?: string;
}

export interface StateEntry {
  code: string;
  name: string;
  category: FormCategory;
  forms?: StateForm[];
  /** For category "none" (and FL/LA which also carry a form) */
  message?: string;
  /** Extra client-facing note (NC, OK, NY) */
  note?: string;
}

/** Hosted path for the fillable Diligent Effort Form. */
export const GENERIC_FORM_HOSTED_PATH =
  '/forms/create-diligent-search/generic/diligent-effort-form.pdf';

/** Runtime availability of the generic Diligent Effort Form asset. */
export const GENERIC_FORM_IS_HOSTED = true;

/**
 * Shared generic form identity. Served from GENERIC_FORM_HOSTED_PATH.
 * Insured/producer pre-fill may be wired later from portal session or policy record.
 * Never pre-fill declination.* fields.
 */
export const GENERIC_FORM = GENERIC_FORM_HOSTED_PATH;

export const STATE_FORM_MAP: StateEntry[] = [
  {
    code: 'AL',
    name: 'Alabama',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'AK',
    name: 'Alaska',
    category: 'state-specific',
    forms: [
      {
        label: "Producing Broker's Surplus Lines Affidavit of Due Diligence",
        sourceUrl:
          'https://www.commerce.alaska.gov/web/portals/11/pub/SurplusLines/08-1236.pdf',
        hostedPath: '/forms/create-diligent-search/AK/alaska-affidavit-due-diligence.pdf',
      },
    ],
  },
  {
    code: 'AZ',
    name: 'Arizona',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'AR',
    name: 'Arkansas',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'CA',
    name: 'California',
    category: 'state-specific',
    forms: [
      {
        label: 'SL-1',
        sourceUrl: 'https://www.slacal.com/brokers/broker-filing-forms/sl-1-form1',
        hostedPath: '/forms/create-diligent-search/CA/california-sl-1.pdf',
      },
      {
        label: 'SL-2',
        sourceUrl:
          'https://www.slacal.com/docs/default-source/general-content-documents/filing-forms/sl2-printable.pdf?sfvrsn=50f360c9_4',
        hostedPath: '/forms/create-diligent-search/CA/california-sl-2.pdf',
      },
    ],
  },
  {
    code: 'CO',
    name: 'Colorado',
    category: 'state-specific',
    forms: [
      {
        label: 'Statement of Diligent Effort (02/2026)',
        sourceUrl:
          'https://slipplus.com/wp-content/uploads/documents/Diligent-Effort-02182026-form.pdf',
        hostedPath: '/forms/create-diligent-search/CO/colorado-statement-of-diligent-effort.pdf',
      },
    ],
  },
  {
    code: 'CT',
    name: 'Connecticut',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'DE',
    name: 'Delaware',
    category: 'state-specific',
    forms: [
      {
        label: 'Statement of Diligent Effort (Effective 2017)',
        sourceUrl:
          'https://insurance.delaware.gov/wp-content/uploads/sites/15/2017/12/SL-1923-DiligentSearchForm-2017.xlsm',
        hostedPath:
          '/forms/create-diligent-search/DE/delaware-sl-1923-statement-of-diligent-effort.pdf',
      },
    ],
  },
  {
    code: 'DC',
    name: 'District of Columbia',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'FL',
    name: 'Florida',
    category: 'none',
    message:
      'Florida does not require a diligent search form. The Surplus Lines Disclosure and Acknowledgement Form is required instead.',
    forms: [
      {
        label: 'Surplus Lines Disclosure and Acknowledgement Form',
        sourceUrl:
          'https://www.fslso.com/docs/default-source/businessforms/surplus-lines-disclosure-form.pdf',
        hostedPath: '/forms/create-diligent-search/FL/florida-surplus-lines-disclosure.pdf',
      },
    ],
  },
  {
    code: 'GA',
    name: 'Georgia',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'HI',
    name: 'Hawaii',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'ID',
    name: 'Idaho',
    category: 'state-specific',
    forms: [
      {
        label: 'Affidavit of Diligent Search (Sample, fillable)',
        sourceUrl:
          'https://idahosurplusline.org/wp-content/uploads/2025/09/AFFIDAVIT-OF-DILIGENT-SEARCH-Sample-Form-2-4-25-Fillable.pdf',
        hostedPath: '/forms/create-diligent-search/ID/idaho-affidavit-of-diligent-search.pdf',
      },
    ],
  },
  {
    code: 'IL',
    name: 'Illinois',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'IN',
    name: 'Indiana',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'IA',
    name: 'Iowa',
    category: 'optional',
    forms: [
      {
        label: 'Optional Iowa Diligent Search Form',
        sourceUrl: 'https://iid.iowa.gov/media/2956/download?inline',
        hostedPath: '/forms/create-diligent-search/IA/iowa-optional-diligent-search.pdf',
      },
      { label: 'Generic Diligent Search', useGenericTemplate: true },
    ],
  },
  {
    code: 'KS',
    name: 'Kansas',
    category: 'state-specific',
    forms: [
      {
        label: 'Statement of Insured',
        sourceUrl:
          'https://www.insurance.kansas.gov/home/showpublisheddocument/5548/639262748233570000',
        hostedPath: '/forms/create-diligent-search/KS/kansas-statement-of-insured.pdf',
      },
      { label: 'Generic Diligent Search', useGenericTemplate: true },
    ],
  },
  {
    code: 'KY',
    name: 'Kentucky',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'LA',
    name: 'Louisiana',
    category: 'none',
    message:
      'Louisiana does not require a diligent search. An affidavit is required for personal lines only, not commercial lines.',
    forms: [
      {
        label: 'Form 438',
        sourceUrl:
          'https://ldi.la.gov/docs/default-source/documents/taxes/form-438.pdf?sfvrsn=f4504552_0',
        hostedPath: '/forms/create-diligent-search/LA/louisiana-form-438.pdf',
      },
    ],
  },
  {
    code: 'ME',
    name: 'Maine',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'MD',
    name: 'Maryland',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'MA',
    name: 'Massachusetts',
    category: 'optional',
    forms: [
      {
        label: 'BR-7 Affidavit',
        sourceUrl: 'https://www.mass.gov/doc/surpaffidavitpdf/download',
        hostedPath: '/forms/create-diligent-search/MA/massachusetts-br-7-affidavit.pdf',
        confirmNote:
          'The Company/NAIC section is for surplus lines carriers and/or admitted carriers who participated on the risk — not admitted carriers alone.',
      },
      { label: 'Generic Diligent Search', useGenericTemplate: true },
    ],
  },
  {
    code: 'MI',
    name: 'Michigan',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'MN',
    name: 'Minnesota',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'MS',
    name: 'Mississippi',
    category: 'none',
    message: 'Mississippi does not require a diligent search.',
  },
  {
    code: 'MO',
    name: 'Missouri',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'MT',
    name: 'Montana',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'NE',
    name: 'Nebraska',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'NV',
    name: 'Nevada',
    category: 'state-specific',
    forms: [
      {
        label: 'NSLA Diligent Search Statement',
        sourceUrl: 'https://www.nsla.org/public/docs/DILIGENT-SEARCH-STATEMENT.pdf',
        hostedPath: '/forms/create-diligent-search/NV/nevada-nsla-diligent-search-statement.pdf',
      },
    ],
  },
  {
    code: 'NH',
    name: 'New Hampshire',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'NJ',
    name: 'New Jersey',
    category: 'state-specific',
    forms: [
      {
        label: 'NJ Certification of Effort to Place Risk (SLPS-6-CERT)',
        sourceUrl: 'https://www.nj.gov/dobi/division_insurance/pdfs/slps6cert1.pdf',
        hostedPath: '/forms/create-diligent-search/NJ/new-jersey-slps-6-cert.pdf',
      },
    ],
  },
  {
    code: 'NM',
    name: 'New Mexico',
    category: 'state-specific',
    forms: [
      {
        label: 'Producing Broker Affidavit (Exhibit A rev 02-12-14)',
        hostedPath:
          '/forms/create-diligent-search/NM/new-mexico-producing-broker-affidavit-exhibit-a.pdf',
      },
      { label: 'Generic Diligent Search', useGenericTemplate: true },
    ],
  },
  {
    code: 'NY',
    name: 'New York',
    category: 'state-specific',
    note: 'Refer to ELANY rules to determine which affidavit applies.',
    forms: [
      {
        label: 'Part A Affidavit',
        sourceUrl:
          'https://www.elany.org/docs/default-source/forms/61-copy.pdf?sfvrsn=fecc5922_43',
        hostedPath: '/forms/create-diligent-search/NY/new-york-part-a-affidavit.pdf',
      },
      {
        label: 'Part C Affidavit',
        sourceUrl:
          'https://www.elany.org/docs/default-source/forms/85.pdf?sfvrsn=64da1473_5',
        hostedPath: '/forms/create-diligent-search/NY/new-york-part-c-affidavit.pdf',
      },
    ],
  },
  {
    code: 'NC',
    name: 'North Carolina',
    category: 'generic',
    note: 'North Carolina: refer to NCSLA rules regarding the Compliance Acknowledgement.',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'ND',
    name: 'North Dakota',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'OH',
    name: 'Ohio',
    category: 'state-specific',
    forms: [
      {
        label: 'Surplus Lines Statement Form INS4024 (rev 04/2026)',
        hostedPath: '/forms/create-diligent-search/OH/ohio-ins4024.pdf',
      },
    ],
  },
  {
    code: 'OK',
    name: 'Oklahoma',
    category: 'generic',
    note: 'Oklahoma: flood coverage does not require a diligent search. Refer to Oklahoma statutes.',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'OR',
    name: 'Oregon',
    category: 'optional',
    forms: [
      {
        label: 'Diligent Search Statement',
        sourceUrl:
          'https://www.oregonsla.org/sites/default/files/2023-04/diligent-search-statement.pdf',
        hostedPath: '/forms/create-diligent-search/OR/oregon-diligent-search-statement.pdf',
      },
      { label: 'Generic Diligent Search', useGenericTemplate: true },
    ],
  },
  {
    code: 'PA',
    name: 'Pennsylvania',
    category: 'optional',
    forms: [
      {
        label: '1609-PR (rev. 11/24)',
        sourceUrl: 'https://www.pasla.org/Documents/1609-PR%2011-2024.pdf',
        hostedPath: '/forms/create-diligent-search/PA/pennsylvania-1609-pr.pdf',
      },
      { label: 'Generic Diligent Search', useGenericTemplate: true },
    ],
  },
  {
    code: 'RI',
    name: 'Rhode Island',
    category: 'state-specific',
    forms: [
      {
        label: 'Affidavit by Broker / Affidavit by Insured (Bulletin 2018-9, 7/2023)',
        sourceUrl:
          'https://dbr.ri.gov/sites/g/files/xkgbur696/files/2023-07/InsuranceBulletin2018-9%20-%207-20-2023%20form%20update.pdf',
        hostedPath: '/forms/create-diligent-search/RI/rhode-island-affidavits-bulletin-2018-9.pdf',
      },
    ],
  },
  {
    code: 'SC',
    name: 'South Carolina',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'SD',
    name: 'South Dakota',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'TN',
    name: 'Tennessee',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'TX',
    name: 'Texas',
    category: 'optional',
    forms: [
      {
        label: 'SLTX Optional Diligent Effort Form no. 1.1 (rev 01.04.19)',
        sourceUrl:
          'https://www.sltx.org/wp-content/uploads/2019/01/SLTX-Optional-Diligent-Effort-Form-no.-1.1-rev-01.04.19.pdf',
        hostedPath: '/forms/create-diligent-search/TX/texas-sltx-optional-diligent-effort.pdf',
      },
      { label: 'Generic Diligent Search', useGenericTemplate: true },
    ],
  },
  {
    code: 'UT',
    name: 'Utah',
    category: 'state-specific',
    forms: [
      {
        label: 'Evidence of Good Faith Effort to Place',
        sourceUrl:
          'https://22769583.fs1.hubspotusercontent-na1.net/hubfs/22769583/EVIDENCE%20OF%20GOOD%20FAITH%20EFFORT%20TO%20PLACE.docx',
        hostedPath:
          '/forms/create-diligent-search/UT/utah-evidence-of-good-faith-effort.pdf',
      },
    ],
  },
  {
    code: 'VT',
    name: 'Vermont',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'VA',
    name: 'Virginia',
    category: 'none',
    message: 'Virginia does not require a diligent search.',
  },
  {
    code: 'WA',
    name: 'Washington',
    category: 'generic',
    forms: [{ label: 'Generic Diligent Search', useGenericTemplate: true }],
  },
  {
    code: 'WV',
    name: 'West Virginia',
    category: 'state-specific',
    forms: [
      {
        label: 'WV Surplus Lines Due Diligence Form (Form DDF, 9/26)',
        hostedPath: '/forms/create-diligent-search/WV/west-virginia-due-diligence-form.pdf',
      },
    ],
  },
  {
    code: 'WI',
    name: 'Wisconsin',
    category: 'none',
    message: 'Wisconsin does not require a diligent search.',
  },
  {
    code: 'WY',
    name: 'Wyoming',
    category: 'state-specific',
    forms: [
      {
        label: 'Statement of Diligent Effort',
        sourceUrl:
          'https://drive.google.com/file/d/13S-D8XXMNWJ0LVYR7jqjtXsLV1YxC4cb/view',
        hostedPath: '/forms/create-diligent-search/WY/wyoming-statement-of-diligent-effort.pdf',
      },
    ],
  },
];

export const getStateEntry = (code: string): StateEntry | undefined =>
  STATE_FORM_MAP.find((entry) => entry.code === code);

export const getStatesSortedByName = (): StateEntry[] =>
  [...STATE_FORM_MAP].sort((a, b) => a.name.localeCompare(b.name));

/**
 * Resolves the runtime URL for a hosted PDF (does not include externalUrl).
 */
export const getHostedFormUrl = (form: StateForm): string | undefined => {
  if (form.useGenericTemplate) {
    return GENERIC_FORM_HOSTED_PATH;
  }
  return form.hostedPath;
};

/**
 * Resolves the URL the client should open: hosted PDF, generic template, or external link.
 */
export const getFormUrl = (form: StateForm): string | undefined => {
  if (form.useGenericTemplate) {
    return GENERIC_FORM_HOSTED_PATH;
  }
  return form.hostedPath ?? form.externalUrl;
};

/** True when the form opens an external site rather than a MISC-hosted PDF. */
export const isExternalForm = (form: StateForm): boolean =>
  Boolean(form.externalUrl) && !form.hostedPath && !form.useGenericTemplate;

/**
 * A form is available when it has a hosted path, external URL, or generic template,
 * and is not blocked by todo.
 */
export const isFormAvailable = (form: StateForm): boolean => {
  if (form.todo) {
    return false;
  }
  if (form.useGenericTemplate) {
    return GENERIC_FORM_IS_HOSTED;
  }
  return Boolean(form.hostedPath || form.externalUrl);
};

export const getUnavailableReason = (form: StateForm): string => {
  if (form.todo) {
    return form.todo;
  }
  if (form.useGenericTemplate) {
    return 'Generic Diligent Effort Form is not hosted yet.';
  }
  return 'Form not hosted yet — pending ingest of a MISC-hosted copy.';
};
